import React from 'react';
import { siteMeta } from '../data/siteMeta';

function Footer() {
  return (
    <footer className="bg-gray-900 text-white mt-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <h3 className="text-lg font-bold mb-4">About This Tracker</h3>
            <p className="text-gray-400 text-sm">
              The most comprehensive free database of children's online privacy and safety laws —
              125+ laws across US federal, state, and 18 international jurisdictions. Covers COPPA,
              AADC, social media restrictions, age verification, app store accountability,
              AI chatbot safety, and more. {siteMeta.updateLabel} ({siteMeta.updatedOn}); refreshed {siteMeta.cadence.toLowerCase()}.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-bold mb-4">Important Notes</h3>
            <ul className="text-gray-400 text-sm space-y-2">
              <li>• US federal COPPA requires parental consent for under 13</li>
              <li>• FTC COPPA amendments effective April 22, 2026</li>
              <li>• Many state social media laws face ongoing First Amendment challenges</li>
              <li>• SCOTUS upheld Texas age verification law June 2025</li>
              <li>• Requirements vary significantly by jurisdiction and category</li>
              <li>• Last data refresh: {siteMeta.updateLabel} — {siteMeta.updatedOn}</li>
              <li>• Next scheduled review: {siteMeta.nextReview}</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-bold mb-4">Our Ecosystem</h3>
            <ul className="text-gray-400 text-sm space-y-3">
              <li>
                <a
                  href="https://cisomarketplace.com"
                  className="flex items-center gap-2 hover:text-purple-400 transition-colors group"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-store text-purple-500 group-hover:text-purple-400 w-4"></i>
                  <span><strong className="text-gray-300">CISO Marketplace</strong><br /><span className="text-xs">Security tools &amp; resources</span></span>
                </a>
              </li>
              <li>
                <a
                  href="https://cyberadx.network"
                  className="flex items-center gap-2 hover:text-purple-400 transition-colors group"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-network-wired text-purple-500 group-hover:text-purple-400 w-4"></i>
                  <span><strong className="text-gray-300">CyberAdX Network</strong><br /><span className="text-xs">Cybersecurity ad network</span></span>
                </a>
              </li>
              <li>
                <a
                  href="https://microsec.tools"
                  className="flex items-center gap-2 hover:text-purple-400 transition-colors group"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-screwdriver-wrench text-purple-500 group-hover:text-purple-400 w-4"></i>
                  <span><strong className="text-gray-300">MicroSec.Tools</strong><br /><span className="text-xs">Free security micro tools</span></span>
                </a>
              </li>
              <li>
                <a
                  href="https://quantumsecurity.ai"
                  className="flex items-center gap-2 hover:text-purple-400 transition-colors group"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-robot text-purple-500 group-hover:text-purple-400 w-4"></i>
                  <span><strong className="text-gray-300">Quantum Security AI</strong><br /><span className="text-xs">AI-powered security</span></span>
                </a>
              </li>
              <li>
                <a
                  href="https://compliancehub.wiki"
                  className="flex items-center gap-2 hover:text-purple-400 transition-colors group"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-book-open text-purple-500 group-hover:text-purple-400 w-4"></i>
                  <span><strong className="text-gray-300">Compliance Hub</strong><br /><span className="text-xs">Compliance knowledge base</span></span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Micro Tools Showcase */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <h3 className="text-lg font-bold mb-4 text-center">Other Privacy & Compliance Tools</h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <a
              href="https://privacyrights.compliancehub.wiki/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-800 hover:bg-gray-700 rounded-lg p-4 text-center transition-all"
            >
              <div className="text-purple-400 text-sm font-semibold">Privacy Rights</div>
              <div className="text-gray-400 text-xs mt-1">State-by-State Guide</div>
            </a>
            <a
              href="https://biometric.myprivacy.blog/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-800 hover:bg-gray-700 rounded-lg p-4 text-center transition-all"
            >
              <div className="text-purple-400 text-sm font-semibold">Biometric Laws</div>
              <div className="text-gray-400 text-xs mt-1">Compliance Tracker</div>
            </a>
            <a
              href="https://pii.compliancehub.wiki/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-800 hover:bg-gray-700 rounded-lg p-4 text-center transition-all"
            >
              <div className="text-purple-400 text-sm font-semibold">PII Guide</div>
              <div className="text-gray-400 text-xs mt-1">Data Classification</div>
            </a>
            <a
              href="https://notification.breached.company/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-800 hover:bg-gray-700 rounded-lg p-4 text-center transition-all"
            >
              <div className="text-purple-400 text-sm font-semibold">Breach Notice</div>
              <div className="text-gray-400 text-xs mt-1">Requirements Map</div>
            </a>
            <a
              href="https://globalcompliancemap.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-800 hover:bg-gray-700 rounded-lg p-4 text-center transition-all"
            >
              <div className="text-purple-400 text-sm font-semibold">Global Map</div>
              <div className="text-gray-400 text-xs mt-1">Privacy Laws Worldwide</div>
            </a>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 text-sm">
          <p>© 2026 <a href="https://cisomarketplace.com" target="_blank" rel="noopener noreferrer" className="hover:text-purple-400">CISO Marketplace</a> | Part of the <a href="https://cyberadx.network" target="_blank" rel="noopener noreferrer" className="hover:text-purple-400">CyberAdX Network</a> &amp; <a href="https://microsec.tools" target="_blank" rel="noopener noreferrer" className="hover:text-purple-400">MicroSec.Tools</a> ecosystem</p>
          <p className="mt-2">
            This tracker is for informational purposes only and does not constitute legal advice.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
